import { RootState } from "./index";
import {
	getPopInSessionFlagCounts,
	PopInSession,
	PopInSessionsSliceState,
	selectors as popInSessionSelectors,
} from "./slices/popInSessions";
import { createSelector } from "@reduxjs/toolkit";
import usersApi from "./slices/users";
import { uuidToIntId } from "../../../../helpers/uuid";
import assessmentsApi from "./slices/assessments";

export const ALL_FLAG_SEVERITY = ["severe", "major", "minor"] as const;
export type FlagSeverity = (typeof ALL_FLAG_SEVERITY)[number];
export const ALL_ANALYZED_STATUS = ["analyzed", "unanalyzed"] as const;
export type AnalyzedStatus = (typeof ALL_ANALYZED_STATUS)[number];
export type SortField = "studentName" | "assessmentName" | "startTime" | "flagCounts";
export type SortDirection = "asc" | "desc";

type SliceTypesMap = {
	popInSessions: PopInSessionsSliceState;
};

function shiftSelector<SliceName extends keyof SliceTypesMap, Result, Args extends unknown[]>(
	shifter: SliceName | ((sliceState: RootState) => SliceTypesMap[SliceName]),
	selector: (sliceState: SliceTypesMap[SliceName], ...args: Args) => Result,
): (rootState: RootState, ...args: Args) => Result {
	if (typeof shifter === "function") {
		return (state, ...args) => selector(shifter(state), ...args);
	}
	return (state, ...args) => selector(state[shifter], ...args);
}

export const selectPopInSession = shiftSelector("popInSessions", popInSessionSelectors.selectPopInSession);

const SEVERITY_TO_FLAG_LEVEL: Record<FlagSeverity, number> = {
	severe: 3,
	major: 2,
	minor: 1,
};

function sumFlagCounts(flagCounts: Record<FlagSeverity, number>) {
	return Object.values(flagCounts).reduce((prev, curr) => prev + curr, 0);
}

export function isSessionViewed(session: PopInSession) {
	const eventCount = session.events.ids.length;
	const lastEventId = eventCount > 0 ? session.events.ids[eventCount - 1] : undefined;
	const lastEventTimestamp = lastEventId ? session.events.entities[lastEventId]?.timestamp : undefined;
	return (
		session.userSessions.ids.length > 0 ||
		(session.lastViewedTimestamp &&
			(!lastEventTimestamp || new Date(lastEventTimestamp).getTime() <= session.lastViewedTimestamp))
	);
}

type SelectorOptions = {
	studentName?: string;
	severities?: FlagSeverity[];
	analyzedStatuses?: AnalyzedStatus[];
	sortBy?: SortField;
	sortDirection: SortDirection;
	size: number;
	page: number;
};
export const selectPopInSessions = createSelector(
	(state: RootState) => state.popInSessions,
	(state: RootState, { studentName, sortBy }: SelectorOptions) =>
		studentName || sortBy === "studentName" ? state.users : undefined,
	(state: RootState, { sortBy }: SelectorOptions) => (sortBy === "assessmentName" ? state.assessments : undefined),
	(_: RootState, { studentName }: SelectorOptions) => studentName,
	(_: RootState, { severities }: SelectorOptions) => severities,
	(_: RootState, { analyzedStatuses }: SelectorOptions) => analyzedStatuses,
	(_: RootState, { sortBy }: SelectorOptions) => sortBy,
	(_: RootState, { sortDirection }: SelectorOptions) => sortDirection,
	(_: RootState, { size }: SelectorOptions) => size,
	(_: RootState, { page }: SelectorOptions) => page,
	(
		{ sessions, isError, isLoading, fulfilledTimeStamp },
		usersState,
		assessmentsState,
		studentName,
		severities = [],
		analyzedStatuses = [],
		sortBy,
		sortDirection,
		size,
		page,
	) => {
		const findStudentName = (studentId?: string) =>
			usersState && studentId
				? usersApi.endpoints.getUser.select(studentId)({ users: usersState }).data?.name
				: undefined;

		const findAssessmentName = (assessmentId?: string) =>
			assessmentsState && assessmentId
				? assessmentsApi.endpoints.getAssessment.select(uuidToIntId(assessmentId))({ assessments: assessmentsState })
						.data?.name
				: undefined;

		const lowerStudentName = studentName?.toLowerCase();
		const filteredSessions = sessions.ids.filter((sessionId) => {
			const session = sessions.entities[sessionId];
			const studentId = session?.studentId;
			const studentName = findStudentName(studentId);
			const viewed = session ? isSessionViewed(session) : false;
			return (
				studentId &&
				(!lowerStudentName || studentName?.toLowerCase().includes(lowerStudentName)) &&
				(severities.length === 0 ||
					severities.some((severity) =>
						session.events.ids.some(
							(eventId) => session.events.entities[eventId]?.payload?.flagLevel === SEVERITY_TO_FLAG_LEVEL[severity],
						),
					)) &&
				(analyzedStatuses.length === 0 ||
					analyzedStatuses.some((status) => (status === "analyzed" && viewed) || (status === "unanalyzed" && !viewed)))
			);
		});
		if (sortBy) {
			const sortFn = (id1: string, id2: string) => {
				const session1 = sessions.entities[id1];
				const session2 = sessions.entities[id2];
				switch (sortBy) {
					case "studentName":
						return (findStudentName(session1?.studentId) ?? "").localeCompare(
							findStudentName(session2?.studentId) ?? "",
						);
					case "assessmentName":
						return (findAssessmentName(session1?.assessmentId) ?? "").localeCompare(
							findAssessmentName(session2?.assessmentId) ?? "",
						);
					case "startTime":
						return new Date(session1?.startTime ?? 0).getTime() - new Date(session2?.startTime ?? 0).getTime();
					case "flagCounts":
						return (
							(session1 ? sumFlagCounts(getPopInSessionFlagCounts(session1)) : 0) -
							(session2 ? sumFlagCounts(getPopInSessionFlagCounts(session2)) : 0)
						);
					default:
						return 0;
				}
			};
			filteredSessions.sort((id1, id2) => sortFn(id1, id2) * (sortDirection === "desc" ? -1 : 1));
		}
		return {
			sessionIds: {
				results: filteredSessions.slice(page * size, (page + 1) * size),
				totalElements: filteredSessions.length,
				totalPages: Math.ceil(filteredSessions.length / size),
			},
			isError,
			isLoading,
			fulfilledTimeStamp,
		};
	},
);
